import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "../../../components/SEO/SEO";
import PublicHeader from "../../../components/PublicHeader/PublicHeader";
import PublicFooter from "../../../components/PublicFooter/PublicFooter";
import {
  ApiError,
  getFamilyDoctorPublicProfile,
  FamilyDoctorPublicProfile,
} from "../../../api/familyDoctors";

import "./FamilyDoctorProfilePage.css";

type ProfileSection = {
  title: string;
  body: React.ReactNode;
};

function asNumberId(id?: string): number | null {
  if (!id) return null;
  const n = parseInt(id, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function joinOrDash(items?: string[] | null): string {
  if (!items || !items.length) return "—";
  return items.join(", ");
}

function formatWebsiteLabel(url: string): string {
  try {
    const u = new URL(url);
    const path =
      u.pathname && u.pathname !== "/" ? u.pathname.replace(/\/$/, "") : "";
    return `${u.hostname}${path}`;
  } catch {
    return url;
  }
}

function formatPhoneForTel(phone: string): string {
  // Strip non-numeric characters for tel: link
  return phone.replace(/[^\d+]/g, "");
}

function buildGoogleMapsSearchUrl(parts: (string | null | undefined)[]): string {
  const address = parts.filter(Boolean).join(", ");
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    address
  )}`;
}

function buildGoogleMapsDirectionsByAddressUrl(
  parts: (string | null | undefined)[]
): string {
  const address = parts.filter(Boolean).join(", ");
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
    address
  )}`;
}

function buildGoogleMapsEmbedByAddressUrl(
  parts: (string | null | undefined)[]
): string {
  // Lightweight embed that does not require an API key.
  // Uses the same clinic address string (no coordinate display anywhere in UI).
  const address = parts.filter(Boolean).join(", ");
  return `https://maps.google.com/maps?q=${encodeURIComponent(
    address
  )}&z=15&output=embed`;
}

function genderIcon(g?: string | null): string {
  const val = String(g || "").trim().toLowerCase();
  if (!val) return "";
  if (val.startsWith("m")) return "♂";
  if (val.startsWith("f")) return "♀";
  return "⚥";
}

function genderClass(g?: string | null): string {
  const val = String(g || "").trim().toLowerCase();
  if (val.startsWith("f")) return "ocf-gender-inline--female";
  if (val.startsWith("m")) return "ocf-gender-inline--male";
  return "ocf-gender-inline--other";
}

function doctorAvatarSrc(g?: string | null): string {
  const val = String(g || "").trim().toLowerCase();
  // Default to male avatar if gender is missing/unknown (keeps UI consistent)
  if (val.startsWith("f")) return "/female doctor.png";
  return "/male doctor.png";
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function localYMD(d: Date): string {
  // YYYY-MM-DD in local time (avoids UTC date shift)
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function formatDayDate(d: Date): string {
  // e.g., "Monday, Feb 9"
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

function formatHHMMToLocalTimeLabel(d: Date, hhmm: string): string {
  const [hh, mm] = String(hhmm || "").split(":");
  const h = parseInt(hh, 10);
  const m = parseInt(mm, 10);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return hhmm;

  const dt = new Date(d);
  dt.setHours(h, m, 0, 0);

  return dt.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

type DayHours = {
  isClosed: boolean;
  open?: string;
  close?: string;
};

function isDateInRangeInclusive(
  ymd: string,
  start: string | null,
  end: string | null
) {
  if (!start || !end) return false;
  // Works for YYYY-MM-DD lexical compare
  return ymd >= start && ymd <= end;
}

function pickTodayClinicStatus(profile: FamilyDoctorPublicProfile | null) {
  const today = new Date();
  const todayYMD = localYMD(today);
  const dayIndex = today.getDay(); // 0=Sun..6=Sat

  const closures = profile?.ClinicHoursMeta?.Closures || [];
  const closure = closures.find(
    (c) => String(c?.date || "").trim() === todayYMD
  );

  if (closure) {
    return {
      label: `Today — ${formatDayDate(today)}: Closed${
        closure.reason ? ` (${closure.reason})` : ""
      }`,
      isClosed: true,
    };
  }

  const override = profile?.ClinicHoursMeta?.TemporaryOverride || null;
  const overrideActive = override
    ? isDateInRangeInclusive(todayYMD, override.start, override.end)
    : false;

  if (overrideActive) {
    const byDay: Record<string, DayHours> = (override?.hoursByDay || {}) as any;
    const dh: DayHours | undefined = byDay[String(dayIndex)] as any;

    if (dh && dh.isClosed) {
      return { label: `Today — ${formatDayDate(today)}: Closed`, isClosed: true };
    }

    if (dh && dh.open && dh.close) {
      const open = formatHHMMToLocalTimeLabel(today, dh.open);
      const close = formatHHMMToLocalTimeLabel(today, dh.close);
      return {
        label: `Today — ${formatDayDate(today)}: Open ${open} – ${close}`,
        isClosed: false,
      };
    }
  }

  const regular = profile?.ClinicRegularHours || [];
  const rh = regular.find((x) => x?.dayOfWeek === dayIndex);

  if (!rh || rh.isClosed) {
    return { label: `Today — ${formatDayDate(today)}: Closed`, isClosed: true };
  }

  if (rh.open && rh.close) {
    const open = formatHHMMToLocalTimeLabel(today, rh.open);
    const close = formatHHMMToLocalTimeLabel(today, rh.close);
    return {
      label: `Today — ${formatDayDate(today)}: Open ${open} – ${close}`,
      isClosed: false,
    };
  }

  return { label: `Today — ${formatDayDate(today)}: Open`, isClosed: false };
}

function pickTodayWalkInStatus(profile: FamilyDoctorPublicProfile | null) {
  const today = new Date();
  const dayIndex = today.getDay();
  const walkIn = profile?.ClinicHoursMeta?.WalkIn || null;

  if (!walkIn || !walkIn.enabled) return null;

  const byDay: Record<string, DayHours> = (walkIn.hoursByDay || {}) as any;
  const dh: DayHours | undefined = byDay[String(dayIndex)] as any;

  if (!dh || dh.isClosed) {
    return `Walk-in today: Closed`;
  }
  if (dh.open && dh.close) {
    const open = formatHHMMToLocalTimeLabel(today, dh.open);
    const close = formatHHMMToLocalTimeLabel(today, dh.close);
    return `Walk-in today: Open ${open} – ${close}`;
  }
  return `Walk-in today: Open`;
}

function dayNameFromIndex(i: number) {
  const names = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  return names[i] || `Day ${i}`;
}

function renderWeeklyHoursTable(
  baseDate: Date,
  rows: {
    dayOfWeek: number;
    isClosed: boolean;
    open: string | null;
    close: string | null;
  }[]
) {
  const sorted = rows
    .slice()
    .sort((a, b) => (a.dayOfWeek || 0) - (b.dayOfWeek || 0));
  return (
    <div className="ocf-lines">
      {sorted.map((r) => {
        const label = dayNameFromIndex(r.dayOfWeek);
        if (r.isClosed) {
          return (
            <div key={`rh-${r.dayOfWeek}`}>
              <b>{label}:</b> Closed
            </div>
          );
        }
        if (r.open && r.close) {
          const open = formatHHMMToLocalTimeLabel(baseDate, r.open);
          const close = formatHHMMToLocalTimeLabel(baseDate, r.close);
          return (
            <div key={`rh-${r.dayOfWeek}`}>
              <b>{label}:</b> {open} – {close}
            </div>
          );
        }
        return (
          <div key={`rh-${r.dayOfWeek}`}>
            <b>{label}:</b> Open
          </div>
        );
      })}
    </div>
  );
}

function renderHoursByDayTable(baseDate: Date, hoursByDay: Record<string, any>) {
  const indices = [0, 1, 2, 3, 4, 5, 6];
  return (
    <div className="ocf-lines">
      {indices.map((i) => {
        const label = dayNameFromIndex(i);
        const dh = hoursByDay ? hoursByDay[String(i)] : null;

        if (!dh || dh.isClosed) {
          return (
            <div key={`oh-${i}`}>
              <b>{label}:</b> Closed
            </div>
          );
        }

        if (dh.open && dh.close) {
          const open = formatHHMMToLocalTimeLabel(baseDate, dh.open);
          const close = formatHHMMToLocalTimeLabel(baseDate, dh.close);
          return (
            <div key={`oh-${i}`}>
              <b>{label}:</b> {open} – {close}
            </div>
          );
        }

        return (
          <div key={`oh-${i}`}>
            <b>{label}:</b> Open
          </div>
        );
      })}
    </div>
  );
}

function CollapsibleSection({
  title,
  subtitle,
  defaultOpen,
  children,
}: {
  title: string;
  subtitle?: string | null;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState<boolean>(!!defaultOpen);

  return (
    <div className="ocf-subcard" style={{ padding: 12 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="ocf-btn ocf-btn--full ocf-btn--compact"
        style={{
          justifyContent: "space-between",
          background: "#fff",
          borderColor: "rgba(0,0,0,0.10)",
        }}
        aria-expanded={open}
      >
        <span style={{ textAlign: "left" }}>
          <b>{title}</b>
          {subtitle ? (
            <span
              className="ocf-muted"
              style={{ display: "block", fontWeight: 500 }}
            >
              {subtitle}
            </span>
          ) : null}
        </span>
        <span aria-hidden="true" style={{ fontWeight: 800 }}>
          {open ? "–" : "+"}
        </span>
      </button>

      {open ? <div style={{ marginTop: 10 }}>{children}</div> : null}
    </div>
  );
}

export default function FamilyDoctorProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const numericId = useMemo(() => asNumberId(id), [id]);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<FamilyDoctorPublicProfile | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        setLoading(true);
        setError(null);

        if (!numericId) {
          setProfile(null);
          setError("Invalid profile ID.");
          return;
        }

        const data = await getFamilyDoctorPublicProfile(numericId);
        if (cancelled) return;

        setProfile(data);
      } catch (e: any) {
        if (cancelled) return;

        if (e instanceof ApiError) {
          setError(e.message || "Failed to load profile.");
        } else {
          setError("Failed to load profile.");
        }
        setProfile(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [numericId]);

  const acceptingNewPatients = !!profile?.AcceptingNewPatients;

  const Pill = ({ ok }: { ok: boolean }) => (
    <span className={`ocf-pill ${ok ? "ocf-pill--ok" : "ocf-pill--neutral"}`}>
      <span className="ocf-pill-dot" aria-hidden="true" />
      {ok ? "Accepting new patients" : "Not currently accepting new patients"}
    </span>
  );

  const InfoPill = ({ text }: { text: string }) => (
    <span className="ocf-infopill">{text}</span>
  );

  const doctorName =
    profile?.FullName || (loading ? "Loading…" : "Family Doctor Profile");
  const clinicName = profile?.Clinic?.ClinicName ?? "—";
  const cityName = profile?.Clinic?.CityName ?? "—";
  const gender = profile?.Gender;

  const seoFallbackTitle = "Family Doctor Profile";
  const seoFallbackDescription =
    "View family doctor availability and clinic information on AcceptingMD.";

  let seoTitle = seoFallbackTitle;
  let seoDescription = seoFallbackDescription;
  let seoNoIndex = false;

  if (!loading && (error || !profile)) {
    seoNoIndex = true;
  } else if (profile) {
    const fullName = String(profile.FullName || "").trim() || "Family Doctor";
    const clinic = String(profile.Clinic?.ClinicName || "").trim();
    const city = String(profile.Clinic?.CityName || "").trim();
    const availability = profile.AcceptingNewPatients
      ? "Currently accepting new patients."
      : "Not currently accepting new patients.";

    seoTitle = city
      ? `${fullName} – Family Doctor in ${city}`
      : `${fullName} – Family Doctor`;

    let roleClause = "family doctor";
    if (clinic && city) {
      roleClause = `family doctor at ${clinic} in ${city}`;
    } else if (clinic) {
      roleClause = `family doctor at ${clinic}`;
    } else if (city) {
      roleClause = `family doctor in ${city}`;
    }

    seoDescription = `${fullName}, ${roleClause}. ${availability} View clinic details, languages, visit types, accessibility, and services.`;
  }

  const profileUrl = profile
    ? `https://acceptingmd.ca/public/family-doctor/${profile.FamilyDoctorID}`
    : null;

  let structuredData: Record<string, unknown> | null = null;

  if (!loading && !error && profile && profileUrl) {
    const fullName = String(profile.FullName || "").trim() || "Family Doctor";
    const clinic = String(profile.Clinic?.ClinicName || "").trim();
    const city = String(profile.Clinic?.CityName || "").trim();
    const phone = String(profile.Clinic?.Phone || "").trim();
    const website = String(profile.Clinic?.Website || "").trim();
    const addressLine1 = String(profile.Clinic?.AddressLine1 || "").trim();
    const addressLine2 = String(profile.Clinic?.AddressLine2 || "").trim();
    const postalCode = String(profile.Clinic?.PostalCode || "").trim();
    const languages = Array.isArray(profile.Languages)
      ? profile.Languages.map((l) => String(l || "").trim()).filter(Boolean)
      : [];

    const availability = profile.AcceptingNewPatients
      ? "Currently accepting new patients."
      : "Not currently accepting new patients.";

    let roleClause = "is a family doctor";
    if (clinic && city) {
      roleClause = `is a family doctor at ${clinic} in ${city}`;
    } else if (clinic) {
      roleClause = `is a family doctor at ${clinic}`;
    } else if (city) {
      roleClause = `is a family doctor in ${city}`;
    }

    const doctorDescription = `${fullName} ${roleClause}. ${availability}`;

    const streetAddress = [addressLine1, addressLine2].filter(Boolean).join(", ");
    const hasMeaningfulAddress = !!(
      streetAddress ||
      city ||
      postalCode
    );

    const clinicEntity: Record<string, unknown> = {
      "@type": "MedicalClinic",
      "@id": `${profileUrl}#clinic`,
    };

    if (clinic) {
      clinicEntity.name = clinic;
    }
    if (phone) {
      clinicEntity.telephone = phone;
    }
    if (website) {
      clinicEntity.url = website;
    }
    if (hasMeaningfulAddress) {
      const address: Record<string, unknown> = {
        "@type": "PostalAddress",
        addressRegion: "Ontario",
        addressCountry: "CA",
      };
      if (streetAddress) {
        address.streetAddress = streetAddress;
      }
      if (city) {
        address.addressLocality = city;
      }
      if (postalCode) {
        address.postalCode = postalCode;
      }
      clinicEntity.address = address;
    }

    const doctorEntity: Record<string, unknown> = {
      "@type": "Person",
      "@id": `${profileUrl}#doctor`,
      name: fullName,
      url: profileUrl,
      jobTitle: "Family Doctor",
      description: doctorDescription,
      worksFor: {
        "@id": `${profileUrl}#clinic`,
      },
    };

    if (languages.length > 0) {
      doctorEntity.knowsLanguage = languages;
    }

    structuredData = {
      "@context": "https://schema.org",
      "@graph": [doctorEntity, clinicEntity],
    };
  }

  const canRequest =
    !!profile && acceptingNewPatients && !loading && !!numericId;

  function goToRequest() {
    if (!numericId) return;
    navigate(`/public/family-doctor/${numericId}/request`);
  }

  const addressParts = [
    profile?.Clinic?.AddressLine1,
    profile?.Clinic?.AddressLine2,
    profile?.Clinic?.CityName,
    profile?.Clinic?.PostalCode,
  ];
  const addressDisplay = addressParts.filter(Boolean).join(", ") || "—";
  const hasAddress = addressParts.some(Boolean);

  const clinicSettingName = profile?.Clinic?.ClinicSettingName || null;

  const todayClinicStatus = useMemo(
    () => (profile ? pickTodayClinicStatus(profile) : null),
    [profile]
  );

  const todayWalkInStatus = useMemo(
    () => (profile ? pickTodayWalkInStatus(profile) : null),
    [profile]
  );

  const today = useMemo(() => new Date(), []);
  const closures = profile?.ClinicHoursMeta?.Closures || [];
  const override = profile?.ClinicHoursMeta?.TemporaryOverride || null;
  const walkIn = profile?.ClinicHoursMeta?.WalkIn || null;

  const sections: ProfileSection[] = [
    {
      title: "About this clinic",
      body: (
        <div>
          {profile ? (
            <div className="ocf-subcard">
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  alignItems: "stretch",
                  flexWrap: "wrap",
                }}
              >
                {/* Left: clinic details */}
                <div style={{ flex: "1 1 360px", minWidth: 280 }}>
                  <div className="ocf-kv">
                    <div>
                      <b>Clinic:</b> {clinicName}
                    </div>
                    <div>
                      <b>City:</b> {cityName}
                    </div>
                    {clinicSettingName ? (
                      <div>
                        <b>Clinic type:</b> {clinicSettingName}
                      </div>
                    ) : null}

                    <div>
                      <b>Address:</b>{" "}
                      {hasAddress ? (
                        <a
                          href={buildGoogleMapsSearchUrl(addressParts)}
                          target="_blank"
                          rel="noreferrer"
                          className="ocf-link"
                        >
                          {addressDisplay}
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>

                    <div>
                      <b>Phone:</b>{" "}
                      {profile.Clinic?.Phone ? (
                        <a
                          href={`tel:${formatPhoneForTel(profile.Clinic.Phone)}`}
                          className="ocf-link"
                        >
                          {profile.Clinic.Phone}
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>

                    <div>
                      <b>Fax:</b> {profile.Clinic?.Fax || "—"}
                    </div>

                    <div>
                      <b>Website:</b>{" "}
                      {profile.Clinic?.Website ? (
                        <a
                          href={profile.Clinic.Website}
                          target="_blank"
                          rel="noreferrer"
                          className="ocf-link"
                        >
                          {formatWebsiteLabel(profile.Clinic.Website)}
                        </a>
                      ) : (
                        "—"
                      )}
                    </div>

                    <div>
                      <b>Coverage:</b> {joinOrDash(profile.CoverageTypes)}
                    </div>
                    <div>
                      <b>Accessibility:</b>{" "}
                      {joinOrDash(profile.AccessibilityFeatures)}
                    </div>
                    <div>
                      <b>On-site services:</b>{" "}
                      {joinOrDash(profile.OnSiteServices)}
                    </div>
                  </div>
                </div>

                {/* Right: map card */}
                <div style={{ flex: "0 1 360px", minWidth: 280 }}>
                  <div className="ocf-subcard" style={{ padding: 10 }}>
                    {hasAddress ? (
                      <>
                        <div style={{ borderRadius: 10, overflow: "hidden" }}>
                          <iframe
                            title="Clinic map"
                            src={buildGoogleMapsEmbedByAddressUrl(addressParts)}
                            width="100%"
                            height="220"
                            style={{ border: 0 }}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                          />
                        </div>

                        <div style={{ marginTop: 10 }}>
                          <a
                            href={buildGoogleMapsDirectionsByAddressUrl(
                              addressParts
                            )}
                            target="_blank"
                            rel="noreferrer"
                            className="ocf-btn ocf-btn--primary ocf-btn--full ocf-btn--compact"
                            style={{ textDecoration: "none" }}
                          >
                            Directions
                          </a>
                        </div>
                      </>
                    ) : (
                      <div className="ocf-muted">Map unavailable.</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="ocf-muted">
              {loading ? "Loading clinic details…" : "No details available."}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Clinic hours",
      body: (
        <div className="ocf-subcard">
          {!profile ? (
            <div className="ocf-muted">
              {loading ? "Loading hours…" : "No hours available."}
            </div>
          ) : (
            <div className="ocf-section-stack">
              {/* 1) Today */}
              <div className="ocf-callout">
                <b>
                  {todayClinicStatus?.label ||
                    `Today — ${formatDayDate(new Date())}`}
                </b>
                {todayWalkInStatus ? (
                  <div className="ocf-muted" style={{ marginTop: 6 }}>
                    {todayWalkInStatus}
                  </div>
                ) : null}
              </div>

              {/* 2) Regular Clinic Hours (collapsed) */}
              <CollapsibleSection title="Regular clinic hours" defaultOpen={false}>
                {renderWeeklyHoursTable(today, profile.ClinicRegularHours || [])}
              </CollapsibleSection>

              {/* 3) Special hours (collapsed) */}
              {override && override.start && override.end ? (
                <CollapsibleSection
                  title="Special hours"
                  subtitle={`Effective ${override.start} – ${override.end}`}
                  defaultOpen={false}
                >
                  <div className="ocf-muted" style={{ marginBottom: 10 }}>
                    These hours temporarily replace the clinic's regular hours
                    during this period.
                  </div>
                  {override.notePublic ? (
                    <div className="ocf-muted" style={{ marginBottom: 10 }}>
                      {override.notePublic}
                    </div>
                  ) : null}
                  {renderHoursByDayTable(today, override.hoursByDay as any)}
                </CollapsibleSection>
              ) : null}

              {/* 4) Walk-in hours (collapsed; only if enabled) */}
              {walkIn && walkIn.enabled ? (
                <CollapsibleSection
                  title="Walk-in hours"
                  subtitle="Walk-in availability may differ from clinic hours."
                  defaultOpen={false}
                >
                  {walkIn.notePublic ? (
                    <div className="ocf-muted" style={{ marginBottom: 10 }}>
                      {walkIn.notePublic}
                    </div>
                  ) : null}
                  {renderHoursByDayTable(today, walkIn.hoursByDay as any)}
                </CollapsibleSection>
              ) : null}

              {/* 5) Holiday closures (collapsed) */}
              {closures && closures.length > 0 ? (
                <CollapsibleSection title="Holiday closures" defaultOpen={false}>
                  <div className="ocf-lines">
                    {closures.map((c, idx) => (
                      <div key={`cl-${idx}`}>
                        <b>{c.date}:</b> Closed
                        {c.reason ? ` (${c.reason})` : ""}
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              ) : null}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "How to submit a request",
      body: (
        <div className="ocf-subcard">
          <div className="ocf-section-stack">
            <div>
              <b>The clinic reviews each request based on its current capacity, practice scope, and intake criteria.</b>
            </div>

            <div className="ocf-muted">
              If you choose to submit a request, you'll provide basic contact and request information for the clinic to review. The standard online request does not ask for medical records or a detailed medical history.
            </div>

            <div className="ocf-callout">
              <b>Important:</b> Submitting a request does not guarantee attachment to the practice.
              The clinic may invite you for a Meet & Greet, place your request on a waitlist, or be unable to accept your request.
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="ocf-profile-page">
      <SEO
        title={seoTitle}
        description={seoDescription}
        noIndex={seoNoIndex}
      />
      {structuredData && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify(structuredData).replace(/</g, "\\u003c")}
          </script>
        </Helmet>
      )}
      <PublicHeader variant="solid" />

      <div className="ocf-profile-container">
        {/* Top nav */}
        <div style={{ marginBottom: 14 }}>
          <Link to="/public/find-family-doctor" className="ocf-profile-back">
            ← Back to results
          </Link>
        </div>

        {/* Header / Doctor section */}
        <div className="ocf-card">
          <div className="ocf-profile-header-top">
            <div className="ocf-doctor-head">
              {/* ✅ Doctor avatar (public/) */}
              <img
                src={doctorAvatarSrc(gender)}
                alt={gender ? `${gender} doctor avatar` : "Doctor avatar"}
                className="ocf-doctor-avatar"
              />

              <div className="ocf-doctor-meta">
                <h1 className="ocf-profile-title ocf-profile-title--doctor">
                  {doctorName}
                  {gender ? (
                    <span
                      className={`ocf-gender-inline ${genderClass(gender)}`}
                      aria-label={`Doctor gender: ${gender}`}
                    >
                      <span className="ocf-gender-symbol" aria-hidden="true">
                        {genderIcon(gender)}
                      </span>
                      {gender}
                    </span>
                  ) : null}
                </h1>

                <div className="ocf-profile-subtitle">
                  {profile ? (
                    <>
                      {clinicName} • {cityName}
                    </>
                  ) : (
                    <>Loading profile…</>
                  )}
                </div>

                {profile ? (
                  <div className="ocf-tagrow">
                    <span className="ocf-tag">
                      <b>Languages:</b> {joinOrDash(profile.Languages)}
                    </span>
                    <span className="ocf-tag">
                      <b>Visit types:</b> {joinOrDash(profile.VisitTypes)}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Accepting new patients pill: far right */}
            <div className="ocf-availability" aria-live="polite" aria-atomic="true">
              {profile ? (
                <Pill ok={acceptingNewPatients} />
              ) : (
                <InfoPill text={loading ? "Loading…" : "Unavailable"} />
              )}
            </div>
          </div>

          {/* Error banner */}
          {error ? (
            <div className="ocf-banner ocf-banner--error">
              <b>Could not load profile.</b> {error}
            </div>
          ) : !loading && !profile ? (
            <div className="ocf-banner ocf-banner--empty">
              <b>Profile not available.</b> Please go back and select a doctor
              from results.
            </div>
          ) : null}

          {/* ✅ CTA in doctor section, bottom, full width */}
          <div className="ocf-profile-cta ocf-profile-cta--stack">
            {profile && acceptingNewPatients ? (
              <button
                type="button"
                className="ocf-btn ocf-btn--primary ocf-btn--full"
                title="Open request form"
                disabled={!canRequest}
                onClick={goToRequest}
              >
                Request to Become a Patient
              </button>
            ) : (
              <InfoPill
                text={
                  loading
                    ? "Loading availability…"
                    : profile
                    ? "Not currently accepting new patient requests — check back later."
                    : "Profile unavailable."
                }
              />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="ocf-profile-sections">
          {sections.map((s) => (
            <section key={s.title} className="ocf-card ocf-section-card">
              <h2 className="ocf-section-title">{s.title}</h2>
              <div className="ocf-section-body">{s.body}</div>
            </section>
          ))}
        </div>

        {/* Footer note */}
        <div className="ocf-profile-footer">
          This is a public information page. It does not replace medical advice
          or urgent care services.
        </div>
      </div>

      <PublicFooter />
    </div>
  );
}
